import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip
} from "recharts";
import { fetchDashboardData, deleteReport } from '../components/api';
import { useNavigate } from "react-router-dom";
import useTranslation from '../hooks/useTranslation';
import { buildDailyRows, computeEmotionAnalytics } from '../utils/dashboardUtils';
import { EmptyState, MoodBalanceBar, DominantEmotionSummary } from './DashboardWidgets';

// Emoji for emotions
const EMOJI = {
  happy: "😊", angry: "😡", fear: "😨", disgust: "🤢", sad: "😢",
  optimistic: "🤩", hopeful: "🙏", mad: "😠", humiliated: "😔"
};

function getToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function parseTop5(str) {
  if (!str) return [];
  return str.split(",").map(s => {
    const [label, val] = s.trim().split(" ");
    return {
      name: label.charAt(0).toUpperCase() + label.slice(1),
      raw: label.toLowerCase(),
      value: Number(val.replace("%", "")),
      color:
        label === "happy" ? "#FBE12A" :
          label === "angry" ? "#FF7B2C" :
            label === "fear" ? "#A1448E" :
              label === "disgust" ? "#FFC85C" :
                label === "sad" ? "#B167B2" :
                  "#ddd"
    };
  });
}

export default function Dashboard() {
  const [dailyRows, setDailyRows] = useState([]);

  const [aggRes, setAggRes] = useState(null); // backend aggregate RES for non-daily

  const [prevAggRes, setPrevAggRes] = useState(null); // previous period aggregate for trends
  const [todaysOverall, setTodaysOverall] = useState(null); // today's overall average if multiple reports
  const [activeTab, setActiveTab] = useState("Daily");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { t } = useTranslation();

  // removed temporary clean duplicates handler



  // Delete report handler

  const handleDeleteReport = async (reportDate) => {

    if (!window.confirm(t('Are you sure you want to delete this report? This action cannot be undone.'))) {

      return;

    }



    try {

      const userId = localStorage.getItem("neuro_user_id");

      if (!userId) {

        alert(t('User not logged in!'));

        return;

      }



      const res = await deleteReport(userId, reportDate);

      if (res && res.status_code === 200) {

        // Refresh the dashboard data

        const type = activeTab.toLowerCase();

        const payload = { user_id: userId, current_date: getToday(), type };

        const response = await fetchDashboardData(payload);

        const data = response.data?.data || {};

        const records = (data.records || []).filter(r => r && (r.report_date || r.created_at));



        // Set today's overall data if available

        setTodaysOverall(data.todays_overall || null);



        if (activeTab === 'Daily') {
          const rows = buildDailyRows(records);
          setDailyRows(rows);
        } else {

          setAggRes(data);

        }



        alert(t('Report deleted successfully!'));

      } else {

        alert(res?.message || t('Failed to delete report'));

      }

    } catch (error) {

      console.error('Delete report error:', error);

      alert(t('Failed to delete report'));

    }

  };



  useEffect(() => {
    async function fetchDashboard() {
      const user_id = localStorage.getItem("neuro_user_id");
      if (!user_id) {
        setError(t('User not logged in!'));
        setDailyRows([]);

        setAggRes(null);

        setPrevAggRes(null);
        return;
      }
      setLoading(true);
      setError(null);

      try {

        const type = activeTab.toLowerCase();

        const payload = { user_id, current_date: getToday(), type };

        const res = await fetchDashboardData(payload);
        const data = res.data?.data || {};

        const records = (data.records || []).filter(r => r && (r.report_date || r.created_at));

        // Set today's overall data if available

        setTodaysOverall(data.todays_overall || null);

        if (activeTab === 'Daily') {
          const daily = buildDailyRows(records);
          setDailyRows(daily);
          setAggRes(null);
          setPrevAggRes(null);
        } else {

          // Use backend-provided aggregate for non-daily views

          setAggRes(data.aggregate?.RES || null);

          setDailyRows([]);



          // Fetch previous period aggregate for comparison

          const prevDate = (() => {

            const d = new Date();

            if (type === 'weekly') d.setDate(d.getDate() - 7);

            else if (type === 'monthly') d.setDate(d.getDate() - 30);

            else d.setDate(d.getDate() - 365);

            return d.toISOString().slice(0, 10);

          })();

          try {

            const prev = await fetchDashboardData({ user_id, current_date: prevDate, type });

            setPrevAggRes(prev.data?.data?.aggregate?.RES || null);

          } catch (_e) {

            setPrevAggRes(null);

          }

        }

      } catch (e) {

        setError(t('Failed to load dashboard. ') + (e?.response?.data?.message || e.message));

        setDailyRows([]);

        setAggRes(null);

        setPrevAggRes(null);
      }
      setLoading(false);
    }
    fetchDashboard();
    // eslint-disable-next-line
  }, [activeTab]);

  function handleTabClick(tab) {
    setActiveTab(tab);
  }

  // Error UI
  if (error) {
    return (
      <div className="dashboard-root-bg">
        <div className="dashboard-center-box">
          <div className="dashboard-card">
            <div
              style={{
                textAlign: "center",
                marginTop: 60,
                fontSize: 28,
                color: "#d7263d",
                fontWeight: 700,
                fontFamily: "'Poppins', 'Montserrat', 'Segoe UI', Arial, sans-serif",
                letterSpacing: "0.03em",
                background: "linear-gradient(90deg, #fff7f3 70%, #f5e2e0)",
                border: "2px solid #ffd6d6",
                borderRadius: "22px",
                padding: "36px 24px",
                boxShadow: "0 6px 30px 0 rgba(240,40,80,0.11)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 44, marginBottom: 10 }}>🚫</span>
              <span>{error}</span>
              <div style={{
                fontSize: 16,
                color: "#aa3b2b",
                marginTop: 8,
                fontWeight: 400,
                letterSpacing: "0.01em",
                fontFamily: "'Montserrat', 'Segoe UI', Arial, sans-serif"
              }}>
                Please <a href="/login" style={{
                  color: "#f84f4f",
                  fontWeight: 600,
                  textDecoration: "underline",
                  transition: "color 0.2s"
                }}>{t('log in')}</a> to view your dashboard.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard-root-bg">
        <div className="dashboard-center-box">
          <div className="dashboard-card">
            <div style={{
              textAlign: "center",
              marginTop: 60,
              fontSize: 26,
              color: "#333"
            }}>
              <span role="img" aria-label="Loader" style={{ fontSize: 36 }}>⏳</span>
              <div style={{ marginTop: 18, fontFamily: "'Poppins', 'Montserrat', Arial" }}>
                {t('Loading your mental health dashboard...')}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Daily table

  function renderDaily() {

    if (!dailyRows.length) {

      return (

        <div style={{ textAlign: "center", marginTop: 60, fontSize: 20, color: "#999" }}>

          {t('No daily reports yet. Record or upload to see entries here!')}

        </div>

      );

    }

    return (

      <div style={{ marginTop: 20 }}>

        {/* Today's Overall Section */}

        {todaysOverall && (

          <div style={{

            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',

            borderRadius: '12px',

            padding: '20px',

            marginBottom: '20px',

            color: 'white',

            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'

          }}>

            <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: '600' }}>

              📊 {t("Today's Overall")} ({todaysOverall.report_count} {t('reports')})

            </h3>

            <div style={{ fontSize: '14px', opacity: 0.9 }}>

              {todaysOverall.response?.RES?.top_5 || ''}

            </div>

          </div>

        )}

        <div style={{ overflowX: 'auto' }}>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>

            <thead>

              <tr>

                <th style={{ textAlign: 'left', padding: '8px 6px' }}>{t('Date')}</th>

                <th style={{ textAlign: 'center', padding: '8px 6px' }}>{t('Time')}</th>

                <th style={{ textAlign: 'right', padding: '8px 6px' }}>{t('Happy')}</th>

                <th style={{ textAlign: 'right', padding: '8px 6px' }}>{t('Sad')}</th>

                <th style={{ textAlign: 'right', padding: '8px 6px' }}>{t('Angry')}</th>

                <th style={{ textAlign: 'right', padding: '8px 6px' }}>{t('Fear')}</th>

                <th style={{ textAlign: 'right', padding: '8px 6px' }}>{t('Disgust')}</th>

                <th style={{ textAlign: 'right', padding: '8px 6px' }}>{t('Surprise')}</th>

                <th style={{ textAlign: 'center', padding: '8px 6px', width: '80px' }}>{t('Actions')}</th>

              </tr>

            </thead>

            <tbody>

              {dailyRows.map(r => (

                <tr key={r.id || r.date}>



                  <td style={{ padding: '6px' }}>{r.date}</td>



                  <td style={{ textAlign: 'center', padding: '6px', fontSize: '12px', color: '#666' }}>

                    {r.time}

                  </td>



                  <td style={{ textAlign: 'right', padding: '6px' }}>{r.happy.toFixed(2)}%</td>

                  <td style={{ textAlign: 'right', padding: '6px' }}>{r.sad.toFixed(2)}%</td>

                  <td style={{ textAlign: 'right', padding: '6px' }}>{r.angry.toFixed(2)}%</td>

                  <td style={{ textAlign: 'right', padding: '6px' }}>{r.fear.toFixed(2)}%</td>

                  <td style={{ textAlign: 'right', padding: '6px' }}>{r.disgust.toFixed(2)}%</td>

                  <td style={{ textAlign: 'right', padding: '6px' }}>{r.surprise.toFixed(2)}%</td>

                  <td style={{ textAlign: 'center', padding: '6px' }}>

                    <button

                      onClick={() => handleDeleteReport(r.id || r.date)}

                      style={{

                        background: '#dc3545',

                        color: 'white',

                        border: 'none',

                        borderRadius: '4px',

                        padding: '4px 8px',

                        fontSize: '12px',

                        cursor: 'pointer',

                        transition: 'background 0.2s'

                      }}

                      onMouseOver={(e) => e.target.style.background = '#c82333'}

                      onMouseOut={(e) => e.target.style.background = '#dc3545'}

                      title={t('Delete this report')}

                    >

                      🗑️

                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

    );

  }

  // Weekly render logic
  function renderWeekly() {
    if (!aggRes) {
      return <EmptyState message={t('No weekly data yet. Please upload more voice samples to view your weekly report!')} />;
    }

    const emotions = parseTop5(aggRes.top_5);
    const pieEmotions = emotions.filter(e => e.value > 0);
    const matchedCond = null;
    const gaugeEmoji = "🙂";
    const weekLine = emotions.map(e => e.value);
    const { dominant, stressDelta, posPct } = computeEmotionAnalytics(emotions, prevAggRes, parseTop5);



    return (

      <div className="dash-main" style={{ transition: 'opacity 240ms ease', opacity: 1 }}>
        <div className="dash-left">
          <div className="dash-chart-box">
            <div className="dash-chart-title">
              {t('Mood swings/week')} <span>{emotions[1]?.name || t('Angry')}</span>
            </div>
            <div className="mood-chart">
              <svg width="100%" height="80" viewBox="0 0 280 80">
                <defs>
                  <linearGradient id="mood-grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#FF9733" />
                    <stop offset="100%" stopColor="#20110c" />
                  </linearGradient>
                </defs>
                <polyline
                  fill="url(#mood-grad)"
                  stroke="#FF7B2C"
                  strokeWidth="7"
                  points={weekLine.map((y, i) => `${i * 24},${80 - (y * 3)}`).join(" ")}
                />
              </svg>
            </div>
          </div>

          {/* Compact summary cards */}


          <div className="weekly-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="dash-emotions-box" style={{ padding: 12 }}>

              <div className="dash-emotions-title">{t('Most felt emotion this week')}</div>

              <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>

                <span style={{ fontSize: 42, marginRight: 10 }}>{EMOJI[dominant.raw] || '🙂'}</span>

                <div style={{ fontSize: 18, fontWeight: 700 }}>{dominant.name} {dominant.value.toFixed(2)}%</div>

              </div>

            </div>

            <MoodBalanceBar title={t('Overall mood balance')} posPct={posPct} />

          </div>

          <div className="dash-emotions-box">
            <div className="dash-emotions-title">{t('Top emotions')}</div>
            <div className="dash-emotions-content">
              <ResponsiveContainer width="35%" height={120}>
                <PieChart>
                  <Pie
                    data={pieEmotions}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={32}
                    outerRadius={54}
                    paddingAngle={3}
                  >
                    {pieEmotions.map(entry => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="dash-emotions-list">
                {emotions.slice(0, 4).map(e => (
                  <div className="em-list-row" key={e.name}>
                    <span style={{ color: e.color }}>{e.name}</span>
                    <span style={{ fontWeight: 600 }}>{e.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="dash-right">
          <DominantEmotionSummary period="week" dominant={dominant} stressDelta={stressDelta} EMOJI={EMOJI} />
          <div className="anger-box">
            <div className="anger-label">
              <span>{emotions[1]?.name || t('Angry')}</span>
              <span style={{ fontSize: "2.4rem", fontWeight: 700 }}>
                {emotions[1]?.value || 0}%
              </span>
            </div>
            <div className="anger-sub">
              <span>{t('Frustration')}</span>
              <span>{t('Impatience')}</span>
            </div>
          </div>

          <div className="mental-box">
            <div className="mental-title">{t('Mental state')}</div>
            <div className="mental-gauge">
              <svg width="120" height="60">
                <path d="M20 55 Q60 10 100 55" stroke="#FF9733" strokeWidth="12" fill="none" />
                <path d="M28 54 Q60 25 92 54" stroke="#90C248" strokeWidth="10" fill="none" />
              </svg>
              <div className="mental-emoji" style={{ fontSize: 48 }}>
                {gaugeEmoji}
              </div>
            </div>
            <div className="mental-status">
              {matchedCond || t('—')}

            </div>

          </div>

        </div>

      </div>

    );

  }



  // Monthly render logic
  function renderMonthly() {
    if (!aggRes) {
      return <EmptyState message={t('No monthly data yet. Come back after a few weeks!')} />;
    }

    const emotions = parseTop5(aggRes.top_5);
    const subAngry = [];
    const gaugeEmoji = "🙂";
    const monthLine = emotions.map(e => ({ week: e.name, value: e.value }));
    const { dominant, stressDelta, posPct } = computeEmotionAnalytics(emotions, prevAggRes, parseTop5);



    return (

      <div className="monthly-dashboard-card" style={{ transition: 'opacity 240ms ease' }}>
        <LineChart width={700} height={190} data={monthLine}>
          <XAxis dataKey="week" axisLine={false} tickLine={false} />
          <YAxis hide />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#FBE12A"
            strokeWidth={4}
            dot={{ r: 6, fill: "#FBE12A" }}
          />
        </LineChart>
        <div className="monthly-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>

          <DominantEmotionSummary period="month" dominant={dominant} stressDelta={stressDelta} EMOJI={EMOJI} />

          <MoodBalanceBar title={t('Overall mood balance this month')} posPct={posPct} />          <div className="monthly-emotion-block">
            <div style={{ display: "flex", alignItems: "center" }}>
              <PieChart width={80} height={80}>
                <Pie
                  data={[{ value: emotions[1]?.value }, { value: 100 - (emotions[1]?.value || 0) }]}
                  startAngle={90}
                  endAngle={-270}
                  innerRadius={28}
                  outerRadius={38}
                  dataKey="value"
                >
                  <Cell fill="#FF9733" />
                  <Cell fill="#2B2623" />
                </Pie>
              </PieChart>
              <span style={{ fontSize: 28, marginLeft: 60, fontWeight: 700 }}>
                {emotions[1]?.value}%</span>
              <span style={{ fontSize: 25, fontWeight: 700, marginLeft: 10 }}>
                {emotions[1]?.name}</span>
            </div>
            {subAngry.map((s, i) => (
              <div key={s.label} style={{ marginTop: 12, fontSize: 19, fontWeight: 500 }}>
                <span>{s.label}</span>
                <div className="bar-bg" style={{ width: 160, display: "inline-block", marginLeft: 15 }}>
                  <div className="bar-fill" style={{
                    width: i ? "40%" : "32%",
                    height: 10, borderRadius: 8,
                    background: "linear-gradient(90deg,#FF9733,#FFC85C)"
                  }} />
                </div>
                <span style={{ marginLeft: 8, fontWeight: 700 }}>{i ? "20" : "32"}%</span>
              </div>
            ))}
          </div>
          <div className="monthly-mental-state">
            <div className="mental-title">{t('Mental state')}</div>
            <div className="monthly-state-label">
              <div style={{ fontWeight: 700, fontSize: 28 }}>{t('—')}</div>              <div style={{ textAlign: "center", marginTop: 8 }}>
                <span style={{ fontSize: 85 }}>{gaugeEmoji}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Yearly render logic
  function renderYearly() {
    if (!aggRes) {
      return <EmptyState message={t('No yearly data yet. Stick with it for a year to see your journey!')} />;
    }

    const emotions = parseTop5(aggRes.top_5);
    const subHappy = [];
    const matchedCond = null;
    const yearLine = emotions.map((e, i) => ({ month: e.name, value: e.value }));
    const { dominant, stressDelta, posPct } = computeEmotionAnalytics(emotions, prevAggRes, parseTop5);



    return (

      <div className="yearly-dashboard-card" style={{ transition: 'opacity 240ms ease' }}>
        <h1 style={{ fontSize: 42, fontWeight: 700, marginBottom: 10 }}>{t('Yearly progress')}</h1>
        <LineChart width={860} height={210} data={yearLine}>
          <XAxis dataKey="month" axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke="#FF9733" strokeWidth={4} dot={{ r: 7, fill: "#FF9733" }} />
        </LineChart>
        <div style={{
          display: "flex", justifyContent: "space-around", margin: "30px 0", fontSize: 23, color: "#eee"
        }}>
          <div>
            <div style={{ color: "#bcbcbc", fontSize: 18 }}>{t('Primary emotion')}</div>
            <div style={{ fontWeight: 700, fontSize: 30 }}>{emotions[0]?.name}</div>
          </div>
          <div>
            <div style={{ color: "#bcbcbc", fontSize: 18 }}>{t('Subcategory')}</div>
            <div style={{ fontWeight: 700, fontSize: 30 }}>
              {subHappy[0]?.value || ""} <span style={{ fontSize: 25 }}>{emotions[0]?.value}%</span>
            </div>
          </div>
          <div>
            <div style={{ color: "#bcbcbc", fontSize: 18 }}>{t('Sub-subcategory')}</div>
            <div style={{ fontWeight: 700, fontSize: 30 }}>{subHappy[1]?.value || ""}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '0 auto 24px', maxWidth: 900 }}>
          <DominantEmotionSummary period="year" dominant={dominant} stressDelta={stressDelta} EMOJI={EMOJI} />

          <MoodBalanceBar title={t('Yearly emotional balance')} posPct={posPct} />

        </div>

        <div style={{
          background: "linear-gradient(90deg, #FFA63E, #FF5B1A 80%)",
          borderRadius: 30, margin: "40px auto 0", padding: 36, width: 460,
          display: "flex", alignItems: "center"
        }}>
          <span style={{ fontSize: 70, marginRight: 35 }}>🧍‍♂️</span>
          <div>
            <div style={{ color: "#fff", fontSize: 23 }}>{t('Mental state')}</div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 38 }}>
              {matchedCond || t('—')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render Tabs ──────────────────────────────────────────────
  return (
    <div className="dashboard-root-bg">
      {/* Fixed close button at top-right of page */}
      <button className="dashboard-close-btn fixed-dashboard-close-btn" onClick={() => {
        try { 
          sessionStorage.setItem('suppressgreetingonce', '1'); 
        } catch { }
        navigate("/");
      }} aria-label="Close dashboard">{t('×')}</button>
      <div className="dashboard-center-box">
        <div className="dashboard-card">
          <div className="dashboard-header">
            <span className="dash-title" onClick={() => setActiveTab(activeTab)} style={{ cursor: "pointer" }}>{t('Dashboard')}</span>
            <div className="dash-tabs">
              <button className={`tab ${activeTab === "Daily" ? "active" : ""}`} onClick={() => handleTabClick("Daily")}>{t('Daily')}</button>
              <button className={`tab ${activeTab === "Weekly" ? "active" : ""}`} onClick={() => handleTabClick("Weekly")}>{t('Weekly')}</button>
              <button className={`tab ${activeTab === "Monthly" ? "active" : ""}`} onClick={() => handleTabClick("Monthly")}>{t('Monthly')}</button>
              <button className={`tab ${activeTab === "Yearly" ? "active" : ""}`} onClick={() => handleTabClick("Yearly")}>{t('Yearly')}</button>
            </div>
          </div>
          {activeTab === "Daily" && renderDaily()}
          {activeTab === "Weekly" && renderWeekly()}
          {activeTab === "Monthly" && renderMonthly()}
          {activeTab === "Yearly" && renderYearly()}
        </div>
      </div>
    </div>
  );
}
